import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

type UserPhotoResult = {
  readonly dataUrl: string | null;
  readonly notFound: boolean;
};

type UserDetails = {
  displayName: string;
  mail: string | null;
  jobTitle: string | null;
};

type UserProfile = {
  photo: UserPhotoResult;
  details: UserDetails;
};

/**
 * Acquires an access token for Microsoft Graph API.
 * Centralizes token acquisition to avoid duplicate silent token requests.
 */
async function acquireGraphToken(
  msal: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
): Promise<string> {
  const { accessToken } = await msal.acquireTokenSilent({ account, scopes });
  return accessToken;
}

/**
 * Fetches the user's 64x64 profile photo from Microsoft Graph.
 * @param accessToken - A valid Graph API access token
 * @returns Promise resolving to UserPhotoResult
 */
async function fetchPhoto(accessToken: string): Promise<UserPhotoResult> {
  const res = await fetch(`${GRAPH_BASE}/me/photos/64x64/$value`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  // User has no photo set
  if (res.status === 404) return { dataUrl: null, notFound: true };

  if (!res.ok) {
    const errorText = await res.text().catch(() => 'Unknown error');
    throw new Error(`Graph API photo request returned ${res.status}: ${errorText}`);
  }

  const blob = await res.blob();

  if (!blob.type.startsWith('image/')) throw new Error(`Invalid content type received: ${blob.type}`);

  // Convert to base64 data URL (no need to revoke later)
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read photo blob'));
    reader.readAsDataURL(blob);
  });

  return { dataUrl, notFound: false };
}

/**
 * Fetches user details (displayName, mail, jobTitle) from Microsoft Graph.
 * @param accessToken - A valid Graph API access token
 * @returns Promise resolving to UserDetails
 */
async function fetchDetails(accessToken: string): Promise<UserDetails> {
  const res = await fetch(`${GRAPH_BASE}/me?$select=displayName,mail,jobTitle`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => 'Unknown error');
    throw new Error(`Graph API user details request returned ${res.status}: ${errorText}`);
  }

  return await res.json();
}

/**
 * Fetches the full user profile (photo + details) from Microsoft Graph
 * with a single token acquisition and parallel requests.
 *
 * @param msal - MSAL PublicClientApplication instance
 * @param account - User's AccountInfo
 * @param scopes - Scopes to request token for (e.g. ['User.Read'])
 * @returns Promise resolving to UserProfile with photo and details
 * @throws Error if token acquisition fails or user details fetch fails
 */
async function fetchUserProfile(
  msal: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
): Promise<UserProfile> {
  const accessToken = await acquireGraphToken(msal, account, scopes);

  const [photoResult, detailsResult] = await Promise.allSettled([fetchPhoto(accessToken), fetchDetails(accessToken)]);

  // Photo is non-critical — gracefully degrade to no photo
  const photo: UserPhotoResult =
    photoResult.status === 'fulfilled' ? photoResult.value : { dataUrl: null, notFound: true };

  // Details are critical — throw if unavailable
  if (detailsResult.status === 'rejected') throw new Error(`Failed to fetch user details: ${detailsResult.reason}`);

  return { photo, details: detailsResult.value };
}

export { fetchUserProfile, type UserDetails, type UserPhotoResult, type UserProfile };
